import {
  Dialog,
  DialogTitle,
  Box,
  Typography,
  DialogContent,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Grid,
  TablePagination,
  DialogActions,
  useTheme,
  Divider,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import {
  FundingNeedDetails,
  MatchDetail,
  MatchQuery,
  ScanResponse,
  ScanResult,
} from '../interfaces/scanInterfaces'
import { dataProvider } from '../providers/dataProvider'
import { useCreate, useGetIdentity, useNotification } from '@refinedev/core'
import ReactMarkdown from 'react-markdown'
import ScanContextNav from './ScanContextNav'
import { User } from '@/utils/auth/auth0'

const MATCH_QUALITY_COLORS = [
  '#f44336',
  '#ff9800',
  '#ffeb3b',
  '#4caf50',
  '#2196f3',
]
// Get match quality color based on rank
const getMatchQualityColor = (rank: number) => {
  return MATCH_QUALITY_COLORS[rank - 1] || '#9e9e9e'
}

// Get match quality label based on rank
const getMatchQualityLabel = (rank: number) => {
  const labels = ['Very Poor', 'Poor', 'Average', 'Good', 'Excellent']
  return labels[rank - 1] || 'Unknown'
}
interface Props {
  dialogOpen: boolean
  handleCloseDialog: () => void
  selectedScanBatch: number | null
  scanDate: string
  fundingNeedTitle: string
  orgName: string
  fundingNeedId: number | null
}

export default function ScanDashboardMatchDetails({
  dialogOpen,
  handleCloseDialog,
  selectedScanBatch,
  scanDate,
  fundingNeedTitle,
  orgName,
  fundingNeedId,
}: Props) {
  const notify = useNotification()

  const user = useGetIdentity().data as User

  const [matchDetails, setMatchDetails] = useState<MatchDetail[]>([])
  // Deliberately `number | undefined`, not a 0 default: "unknown" and "none" are
  // different and the UI says so. The provider never hands back NaN.
  const [totalItemsMatchDetails, setTotalItemsMatchDetails] = useState<
    number | undefined
  >(undefined)
  const [totalFilteredMatchDetails, setTotalFilteredMatchDetails] = useState<
    number | undefined
  >(undefined)

  const [matchSearchTerm, setMatchSearchTerm] = useState<string>('')
  const [matchSearchTermApplied, setMatchSearchTermApplied] = useState<
    string | null
  >(null)
  const [showFundingNeedProfile, setShowFundingNeedProfile] =
    useState<boolean>(false)
  const [matchPage, setMatchPage] = useState(0)
  const [matchRowsPerPage, setMatchRowsPerPage] = useState(10)
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false)
  const [loadingFundingNeed, setLoadingFundingNeed] = useState<boolean>(false)

  const [adjustingScoreId, setAdjustingScoreId] = useState<number | null>(null)
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [adjustScoreNotes, setAdjustScoreNotes] = useState<string>('')
  const [savingScore, setSavingScore] = useState<boolean>(false)

  const theme = useTheme()
  const lightOrDarkMode = theme.palette.mode

  const [fundingNeedDetails, setFundingNeedDetails] =
    useState<FundingNeedDetails | null>(null)
  async function fetchMatchDetails() {
    // Use dataProvider to fetch scan results for this batch

    if (!selectedScanBatch) {
      notify?.open?.({
        type: 'error',
        message: 'Failed to load scan dashboard data',
        description: 'Please try again later',
      })
      return
    }

    // Use pagination parameters for the API call
    const startIndex = matchPage * matchRowsPerPage
    const endIndex = startIndex + matchRowsPerPage

    const query = {
      _sort: 'rank',
      _order: 'desc',
      _limit: 1000, // High limit to ensure we get ALL results
      _start: startIndex.toString(),
      _end: endIndex.toString(),
    } as MatchQuery

    // Add search term if available
    if (matchSearchTerm.trim() !== '') {
      query['q'] = matchSearchTerm.trim().toLowerCase()
      setMatchSearchTermApplied(matchSearchTerm.trim())
    }

    if (!dataProvider || !dataProvider.custom) {
      notify?.open?.({
        type: 'error',
        message: 'Failed to load scan dashboard data',
        description: 'Please try again later',
      })
      return
    }

    const response = await dataProvider.custom({
      url: `/scan-results/batch/${selectedScanBatch}`,
      method: 'get',
      query,
    })

    const matchesForBatch = response.data as ScanResult[]

    // Undefined when the total header was unreadable - see totalFrom in the data
    // provider. Never NaN.
    const matchCount = (response as ScanResponse).total

    // Add warning if we might be hitting the limit
    if (matchCount !== undefined && matchCount >= 950) {
      notify?.open?.({
        type: 'error',
        message: 'Warning',
        description:
          'May be hitting API result limit. Some results might be missing.',
      })
    }

    // Process all matches for this batch
    if (matchesForBatch && matchesForBatch.length > 0) {
      const details: MatchDetail[] = matchesForBatch.map(
        (match: ScanResult) => {
          // Parse result JSON if needed
          let reasoning = match.result
          try {
            if (typeof match.result === 'string') {
              const resultObj = JSON.parse(match.result)
              reasoning = resultObj.score
                ? `Score: ${resultObj.score}`
                : match.result
            }
          } catch (e) {
            // If parsing fails, use as-is
            reasoning = match.result || 'No reasoning available'
          }

          // Parse questions JSON if needed
          let questions = match.questions
          try {
            if (typeof match.questions === 'string') {
              const questionsArr = JSON.parse(match.questions)
              questions = Array.isArray(questionsArr)
                ? questionsArr.join('\n\n')
                : match.questions
            }
          } catch (e) {
            // If parsing fails, use as-is
            questions = match.questions || 'No questions available'
          }

          // Create a match detail object with all the information we need
          return {
            id: match.id || 0,
            opportunity_name:
              match.opportunity?.fund_name || 'Unknown Opportunity',
            funder_name: match.funder?.name || 'Unknown Funder',
            rank: match.rank || 0, // Default to 0 if rank is missing
            reasoning: reasoning || 'No reasoning available',
            explanation: match.explanation || 'No explanation available',
            questions: questions || 'No questions available',
            is_relational: Boolean(match.opportunity?.is_relational),
            fund_profile:
              match.opportunity?.profile_text || 'No profile available',
            expanded: false, // Track if details are expanded inline
          }
        }
      )

      setMatchDetails(details)
      if (matchSearchTerm !== '') {
        setTotalFilteredMatchDetails(matchCount) // for pagination on match details dialog
      } else {
        setTotalFilteredMatchDetails(matchCount)
        setTotalItemsMatchDetails(matchCount) // for full total
      }
    } else {
      // If there's no data, show an empty state
      setMatchDetails([])
    }
  }
  useEffect(() => {
    if (!selectedScanBatch) return
    fetchMatchDetails()
  }, [matchPage, matchRowsPerPage, selectedScanBatch])

  // Filter match results by search term
  async function handleSearchMatches(e?: React.FormEvent) {
    e?.preventDefault()
    if (matchSearchTerm.trim() === '') {
      return
    }
    if (!selectedScanBatch) {
      return
    }

    await fetchMatchDetails()
  }

  function handleClearSearchMatches() {
    if (!selectedScanBatch) {
      return
    }
    setMatchSearchTerm('')
    setMatchSearchTermApplied(null)
    fetchMatchDetails()
  }

  // Open dropdown with funding need details - shows org profile and need description for the selected batch
  async function handleOpenFundingNeedDetails(batchNumber: number | null) {
    // Prevent duplicate calls if already loading

    if (loadingFundingNeed) return
    if (!batchNumber) return

    if (!fundingNeedId) {
      notify?.open?.({
        type: 'error',
        message: 'Failed to load funding need details',
        description: 'Please try again later',
      })
      return
    }

    if (showFundingNeedProfile) {
      setShowFundingNeedProfile(false)
      return
    }

    setShowFundingNeedProfile(true)
    setLoadingFundingNeed(true)

    try {
      // Use dataProvider to fetch scan results for this batch

      if (!dataProvider || !dataProvider.custom) {
        notify?.open?.({
          type: 'error',
          message: 'Failed to load scan dashboard data',
          description: 'Please try again later',
        })
        return
      }

      const response = await dataProvider.custom({
        url: `/scan-results/funding-need/${fundingNeedId}`,
        method: 'get',
      })

      const fundingNeedDetailsData = response.data as FundingNeedDetails

      if (fundingNeedDetailsData) {
        setFundingNeedDetails(fundingNeedDetailsData)
      } else {
        // If there's no data, show an empty state
        setFundingNeedDetails(null)
      }
    } catch (err) {
      notify?.open?.({
        type: 'error',
        message: 'Failed to load funding need details',
        description: `It's possible the funding need no long exists. Please try again later otherwise.`,
      })
      // Don't set the main error state, just show an error in the dialog
      setFundingNeedDetails(null)
    } finally {
      setLoadingFundingNeed(false)
    }
  }

  // Toggle match details expansion inline
  const toggleMatchExpansion = (matchId: number) => {
    setMatchDetails((prev) =>
      prev.map((match) => {
        if (match.id === matchId) {
          return { ...match, expanded: !match.expanded }
        }
        return match
      })
    )
    setSelectedScore(null)
    setAdjustingScoreId(null)
  }

  // Handle match pagination change
  const handleMatchPageChange = (event: unknown, newPage: number) => {
    setMatchPage(newPage)
  }

  // Handle match rows per page change
  const handleMatchRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMatchRowsPerPage(parseInt(event.target.value, 10))
    setMatchPage(0)
  }

  const { mutate: adjustMatchScore } = useCreate()

  function handleAdjustScoreSubmit() {
    if (adjustScoreNotes.trim() === '' || !selectedScore) {
      return
    }
    if (!user || user.sub === null || !adjustingScoreId) {
      return
    }

    const currentScore = matchDetails.find(
      (match) => match.id === adjustingScoreId
    )?.rank

    if (!currentScore) {
      return
    }

    if (selectedScore === currentScore) {
      return
    }

    setSavingScore(true)

    adjustMatchScore(
      {
        resource: 'adjust-match-score',
        values: {
          scan_id: adjustingScoreId,
          old_rank: currentScore,
          new_rank: selectedScore,
          notes: adjustScoreNotes,
          admin_name:
            user?.name ||
            `${user?.given_name || ''} ${user?.family_name || ''}`,
        },
      },
      {
        onSuccess: () => {
          setAdjustingScoreId(null)
          setAdjustScoreNotes('')
          setSavingScore(false)
        },
        onError: () => {
          notify?.open?.({
            type: 'error',
            message: 'Error adjusting match score. Please try again.',
          })
          setSavingScore(false)
        },
      }
    )
  }

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' },
        }}
      >
        <DialogTitle>
          {/* GH #1723: persistent, not state-dependent. Without it this dialog was
              a dead end - no route to the A/B comparison for the same scan and no
              way back to the list but the browser's back button. */}
          <ScanContextNav
            needId={fundingNeedId}
            batchNumber={selectedScanBatch}
            current="matches"
            onShowMatches={() => undefined}
            onShowList={handleCloseDialog}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              Match Details
              {selectedScanBatch && (
                <>
                  <Typography variant="subtitle1" color="text.secondary">
                    Batch Number: {selectedScanBatch}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Scan Date: {scanDate}
                  </Typography>
                </>
              )}
            </Box>
            <Typography variant="subtitle2" color="text.secondary">
              {totalItemsMatchDetails === undefined
                ? 'Match count unavailable'
                : `${totalItemsMatchDetails} potential matches`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Funding need information with expand button */}
              <Box sx={{ mb: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      color="text.primary"
                      gutterBottom
                    >
                      <strong>Funding Need:</strong> {fundingNeedTitle}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      <strong>Organisation:</strong> {orgName}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      handleOpenFundingNeedDetails(selectedScanBatch || null)
                    }
                    sx={{ minWidth: '150px' }}
                  >
                    {showFundingNeedProfile
                      ? 'Hide Need Profile'
                      : 'Show Need Profile'}
                  </Button>
                </Box>

                {/* Expandable Funding Need Profile */}
                {showFundingNeedProfile && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: 'white',
                    }}
                  >
                    {loadingFundingNeed && (
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
                      >
                        <CircularProgress />
                      </Box>
                    )}

                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Please note these are the current details of the
                        Organisations Profile and Funding Need selected. It is
                        possible this data has changed since the scan was run.
                      </Typography>

                      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Funding Need Description
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                      >
                        {fundingNeedDetails?.need_description ||
                          'Unknown Description'}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Organisation Profile
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                      >
                        {fundingNeedDetails?.org_profile || 'Unknown Profile'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <form onSubmit={handleSearchMatches}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Search opportunities..."
                      size="small"
                      value={matchSearchTerm}
                      onChange={(e) => setMatchSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{
                        mb: 1.75,
                        ml: 1,
                        height: 40,
                        width: 120,
                      }}
                      disabled={matchSearchTerm.trim() === ''}
                    >
                      Search
                    </Button>
                  </Box>
                </form>
                {matchSearchTermApplied && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing results for "{matchSearchTermApplied}"
                    </Typography>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={handleClearSearchMatches}
                      sx={{ ml: 1 }}
                    >
                      Clear Search
                    </Button>
                  </Box>
                )}

                {matchDetails.length > 0 ? (
                  <>
                    <Typography variant="body2" color="textSecondary">
                      Showing {matchDetails.length} results
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Funder</TableCell>
                            <TableCell>Opportunity</TableCell>
                            <TableCell align="center">Match Score</TableCell>
                            <TableCell align="center">
                              Relational Funding
                            </TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {matchDetails.map((match) => (
                            <React.Fragment key={match.id}>
                              <TableRow>
                                <TableCell>{match.funder_name}</TableCell>
                                <TableCell>{match.opportunity_name}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={`${
                                      match.rank
                                    } - ${getMatchQualityLabel(match.rank)}`}
                                    style={{
                                      backgroundColor: getMatchQualityColor(
                                        match.rank
                                      ),
                                      color:
                                        match.rank <= 2 ? 'white' : 'black',
                                      minWidth: '120px',
                                    }}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={match.is_relational ? 'Yes' : 'No'}
                                    color={
                                      match.is_relational
                                        ? 'success'
                                        : 'default'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      toggleMatchExpansion(match.id)
                                    }
                                  >
                                    {match.expanded
                                      ? 'Hide Details'
                                      : 'Show Details'}
                                  </Button>
                                </TableCell>
                              </TableRow>

                              {/* Expandable row for match details */}
                              {match.expanded && (
                                <TableRow>
                                  <TableCell
                                    colSpan={5}
                                    sx={{ p: 3, backgroundColor: '#f5f5f5' }}
                                  >
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <Box>
                                          <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                          >
                                            Match Result:
                                          </Typography>
                                          <Typography variant="body2" paragraph>
                                            {match.reasoning}
                                          </Typography>
                                        </Box>

                                        <Box sx={{ mt: 2 }}>
                                          <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                          >
                                            Questions to Consider:
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            paragraph
                                            sx={{ whiteSpace: 'pre-line' }}
                                          >
                                            {match.questions}
                                          </Typography>

                                          {match.rank <= 3 && (
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                maxWidth: '250px',
                                              }}
                                            >
                                              <Button
                                                variant="contained"
                                                onClick={() => {
                                                  setAdjustingScoreId(match.id)
                                                  setSelectedScore(match.rank)
                                                  setAdjustScoreNotes('')
                                                }}
                                              >
                                                Manually adjust score
                                              </Button>

                                              {adjustingScoreId ===
                                                match.id && (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2,
                                                    mt: 2,
                                                    bgcolor: 'white',
                                                    width: 550,
                                                    p: 2,
                                                  }}
                                                >
                                                  <FormControl>
                                                    <InputLabel>
                                                      Adjust Score
                                                    </InputLabel>
                                                    <Select
                                                      size="small"
                                                      value={selectedScore}
                                                      onChange={(e) =>
                                                        setSelectedScore(
                                                          Number(e.target.value)
                                                        )
                                                      }
                                                      label="Adjust Score"
                                                      sx={{
                                                        width: 100,
                                                        bgcolor: 'white',
                                                      }}
                                                    >
                                                      {[4, 5].map((score) => (
                                                        <MenuItem
                                                          key={score}
                                                          value={score}
                                                        >
                                                          {score}
                                                        </MenuItem>
                                                      ))}
                                                    </Select>
                                                  </FormControl>
                                                  <FormControl>
                                                    <TextField
                                                      value={adjustScoreNotes}
                                                      label={
                                                        'Why are you making this change?'
                                                      }
                                                      helperText={
                                                        'Please be as specific as possible to help us improve the system'
                                                      }
                                                      multiline
                                                      minRows={3}
                                                      maxRows={10}
                                                      onChange={(e) =>
                                                        setAdjustScoreNotes(
                                                          e.target.value
                                                        )
                                                      }
                                                      sx={{
                                                        bgcolor: 'white',
                                                        width: 500,
                                                        textarea: {
                                                          resize: 'vertical',
                                                        },
                                                      }}
                                                    />
                                                  </FormControl>

                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      justifyContent:
                                                        'flex-end',
                                                      gap: 1,
                                                      width: 500,
                                                    }}
                                                  >
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      onClick={() => {
                                                        setAdjustingScoreId(
                                                          null
                                                        )
                                                        setSelectedScore(null)
                                                        setAdjustScoreNotes('')
                                                      }}
                                                    >
                                                      Cancel
                                                    </Button>
                                                    <Button
                                                      size="small"
                                                      disabled={
                                                        selectedScore ===
                                                          match.rank ||
                                                        adjustScoreNotes.trim() ==
                                                          '' ||
                                                        savingScore
                                                      }
                                                      variant="contained"
                                                      onClick={() => {
                                                        handleAdjustScoreSubmit()
                                                      }}
                                                    >
                                                      {savingScore
                                                        ? 'Saving...'
                                                        : 'Save'}
                                                    </Button>
                                                  </Box>
                                                </Box>
                                              )}
                                            </Box>
                                          )}
                                        </Box>
                                      </Grid>

                                      <Grid item xs={12} md={6}>
                                        <Box>
                                          <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                          >
                                            Fund Profile:
                                          </Typography>
                                          <Box
                                            sx={{
                                              p: 2,
                                              border: '1px solid #e0e0e0',
                                              borderRadius: 1,
                                              backgroundColor: '#fff',
                                              maxHeight: '400px',
                                              overflow: 'auto',
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              paragraph
                                            >
                                              <ReactMarkdown
                                                className={`markdown-${lightOrDarkMode}`}
                                              >
                                                {match.fund_profile}
                                              </ReactMarkdown>
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={totalFilteredMatchDetails ?? -1}
                      rowsPerPage={matchRowsPerPage}
                      page={matchPage}
                      onPageChange={handleMatchPageChange}
                      onRowsPerPageChange={handleMatchRowsPerPageChange}
                    />
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                    sx={{ mt: 2 }}
                  >
                    No match details found
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
